import { apiClient } from '../lib/apiClient'

export type NumericMinutes = number | string

export interface Task {
  id: string
  org_id: string
  project_id: string
  title: string
  description?: string
  task_type?: 'milestone' | 'task' | 'subtask'
  status: 'todo' | 'in_progress' | 'done' | 'blocked'
  priority: 'low' | 'medium' | 'high' | 'critical'
  start_date?: string
  due_date?: string
  estimated_minutes?: NumericMinutes
  actual_minutes?: NumericMinutes
  logged_minutes?: NumericMinutes
  draft_minutes?: NumericMinutes
  parent_task_id?: string
  order_index: number
  created_at: string
  updated_at: string
  created_by: string
  completed_at?: string
  version?: number
  last_modified_by?: string
  last_modified_at?: string
}

export const toMinutesNumber = (minutes?: NumericMinutes | null): number => {
  const value = Number(minutes)
  return Number.isFinite(value) ? value : 0
}

export const minutesToHours = (minutes?: NumericMinutes | null): number => {
  const value = toMinutesNumber(minutes)
  if (!value) return 0
  return Math.round((value / 60) * 100) / 100
}

export const hoursToMinutes = (hours?: number): number => {
  if (!hours) return 0
  return Math.round(hours * 60)
}

export const formatHours = (minutes?: number): string => {
  if (!minutes) return '0h'
  const hours = minutesToHours(minutes)
  return `${hours}h`
}

export interface TaskAssignment {
  id: string
  task_id: string
  user_id: string
  assigned_at: string
  assigned_by: string
}

export interface AssignedTask extends Task {
  project_title?: string
  parent_task_title?: string
}

export interface TaskComment {
  id: string
  org_id: string
  task_id: string
  user_id: string
  comment_text: string
  created_at: string
  updated_at: string
  edited: boolean
}

export interface TaskDependency {
  id: string
  task_id: string
  depends_on_task_id: string
  dependency_type: 'finish_to_start' | 'start_to_start' | 'finish_to_finish'
  created_at: string
}

export interface TaskLoggedHoursBreakdown {
  resource_id: string
  resource_name: string
  email: string
  logged_minutes: NumericMinutes
  draft_minutes: NumericMinutes
  last_entry_date: string | null
}

export const taskService = {
  async validateTaskDates(projectId: string, taskStartDate?: string, taskDueDate?: string): Promise<{ valid: boolean; error?: string }> {
    if (!taskStartDate && !taskDueDate) {
      return { valid: true }
    }
    const params = new URLSearchParams()
    if (taskStartDate) params.set('startDate', taskStartDate)
    if (taskDueDate) params.set('dueDate', taskDueDate)
    return apiClient.get<{ valid: boolean; error?: string }>(
      `/projects/${projectId}/validate-task-dates?${params.toString()}`
    )
  },

  async getProjectTasks(projectId: string): Promise<Task[]> {
    return apiClient.get<Task[]>(`/projects/${projectId}/tasks`)
  },

  async getTaskById(taskId: string): Promise<Task | null> {
    return apiClient.get<Task | null>(`/tasks/${taskId}`)
      .catch((err: any) => err?.status === 404 ? null : Promise.reject(err))
  },

  async createTask(task: Omit<Task, 'id' | 'created_at' | 'updated_at'>): Promise<Task> {
    return apiClient.post<Task>(`/projects/${task.project_id}/tasks`, task)
  },

  async updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
    return apiClient.patch<Task>(`/tasks/${taskId}`, updates)
  },

  async deleteTask(taskId: string): Promise<void> {
    return apiClient.delete(`/tasks/${taskId}`)
  },

  async getTaskComments(taskId: string): Promise<TaskComment[]> {
    return apiClient.get<TaskComment[]>(`/tasks/${taskId}/comments`)
  },

  async addComment(taskId: string, userId: string, orgId: string, comment: string): Promise<TaskComment> {
    return apiClient.post<TaskComment>(`/tasks/${taskId}/comments`, { userId, orgId, comment_text: comment })
  },

  async getTaskDependencies(taskId: string): Promise<TaskDependency[]> {
    return apiClient.get<TaskDependency[]>(`/tasks/${taskId}/dependencies`)
  },

  async addDependency(
    taskId: string,
    dependsOnTaskId: string,
    dependencyType: TaskDependency['dependency_type']
  ): Promise<TaskDependency> {
    return apiClient.post<TaskDependency>(`/tasks/${taskId}/dependencies`, {
      depends_on_task_id: dependsOnTaskId,
      dependency_type: dependencyType,
    })
  },

  async removeDependency(dependencyId: string): Promise<void> {
    return apiClient.delete(`/task-dependencies/${dependencyId}`)
  },

  async reorderTasks(taskIds: string[]): Promise<void> {
    return apiClient.post('/tasks/reorder', { taskIds })
  },

  async getTaskAssignments(taskId: string): Promise<TaskAssignment[]> {
    return apiClient.get<TaskAssignment[]>(`/tasks/${taskId}/assignments`)
  },

  async getAllocatedResources(projectId: string, orgId: string): Promise<any[]> {
    return apiClient.get<any[]>(`/orgs/${orgId}/projects/${projectId}/allocated-resources`)
  },

  async assignResourceToTask(
    taskId: string,
    resourceId: string,
    orgId: string,
    assignedBy: string
  ): Promise<TaskAssignment> {
    return apiClient.post<TaskAssignment>(`/tasks/${taskId}/assignments`, { resourceId, orgId, assignedBy })
  },

  async removeTaskAssignment(assignmentId: string): Promise<void> {
    return apiClient.delete(`/task-assignments/${assignmentId}`)
  },

  async getResourceAssignedTasks(resourceId: string, projectId: string): Promise<Task[]> {
    return apiClient.get<Task[]>(`/projects/${projectId}/tasks?assignedResourceId=${resourceId}`)
  },

  async getAllAssignedTasks(orgId: string, resourceId: string): Promise<AssignedTask[]> {
    return apiClient.get<AssignedTask[]>(`/orgs/${orgId}/resources/${resourceId}/tasks`)
  },

  async getTaskLoggedHoursBreakdown(taskId: string): Promise<TaskLoggedHoursBreakdown[]> {
    return apiClient.get<TaskLoggedHoursBreakdown[]>(`/tasks/${taskId}/logged-hours-breakdown`)
  },
}
